import { getCurrentOperatorName } from '../../utils/currentOperator';
import { calendarsFor, fromIso } from '../../utils/businessDate';
import React from 'react';
import axios from 'axios';
import styled from 'styled-components';
import BaseTicketScreen from './BaseTicketScreen';
import {
  FormRow,
  FormGroup,
  Label,
  Input,
  Select,
  FieldError,
} from './shared/TicketStyledComponents';
import { getTodayDate, getTenantToday } from '../../utils/dateUtils';
import { settlementCalendars } from '../../utils/settlement';
import { formatAmountWithCommas, parseAmountShorthand } from '../../utils/amountUtils';
import { Target } from 'lucide-react';
import {
  BandEyebrow,
  BandHeader,
  BandSubline,
  BandTitle,
  BandTitleGroup,
  StructuredBand,
  StructuredHeroPanel,
  StructuredWorkspace,
  formatCurrencyValue,
} from './shared/StructuredCreditTicketLayout';

import { DateField } from '../shared/DateField';

const InfoNote = styled.div`
  background: rgba(244, 114, 182, 0.1);
  border: 1px solid rgba(244, 114, 182, 0.3);
  border-radius: 14px;
  padding: 14px 16px;
  font-size: 13px;
  color: #fbcfe8;
  line-height: 1.5;
`;

const formatCommitmentSignal = (value: string, currency = 'USD') =>
  formatCurrencyValue(value ? parseAmountShorthand(value) : 0, currency || 'USD');

/**
 * Private Equity commitment ticket — unified IBOR lifecycle.
 *
 * <p>Position-anchored asset class: this screen is BUY-only. The acquisition
 * traverses the standard pre-clear lifecycle (DRAFT → PRECLEAR_PENDING →
 * APPROVED → SENT → EXECUTED → POSTED) via {@link BaseTicketScreen}. The silo
 * row ({@code fact_commitment}) is materialised by
 * {@code CommitmentPostingSpi.ensureSiloFromTrade} only at POST time, populated
 * from the PE metadata columns this ticket stamps onto the trade payload.
 *
 * <p>Capital-call / distribution / MTM / dispose all originate from the
 * commitment dashboard, not from this ticket — see
 * {@code feedback_position_anchored_dispose}.
 */
const PrivateEquityTicketScreen: React.FC = () => {
  const initialFormData = {
    transactionType: 'BUY' as const,
    assetManager: '',
    portfolioManager: '',
    assetClass: 'Private Equity',
    assetSubClass: 'PE Commitment',
    security: '',
    identifier: '',
    portfolio: '',
    amount: '',
    tradeDate: getTodayDate(),
    // PE commitments settle on the commitment date — there's no T+N market
    // convention. Default settle = trade so the fact_positions bi-temporal
    // anchor matches every follow-on lifecycle event (call/dist/MTM/dispose).
    settleDate: getTodayDate(),
    submittedBy: getCurrentOperatorName(),
    currency: 'USD',
    executedBy: '',
    notes: '',
    // PE-specific
    fundName: '',
    strategy: 'BUYOUT',
    fundStructure: 'LIMITED_PARTNERSHIP',
    vintageYear: fromIso(getTenantToday()).getFullYear().toString(),
    gpName: '',
    commitmentDate: getTodayDate(),
    commitmentAmount: '',
    managementFeeRate: '2.0',
    carriedInterestRate: '20.0',
    preferredReturnRate: '8.0',
  };

  const validateForm = (formData: any, action: string): { [key: string]: string } => {
    const errors: { [key: string]: string } = {};

    if (!formData.portfolio?.trim()) errors.portfolio = 'Portfolio is required';
    if (!formData.fundName?.trim()) errors.fundName = 'Fund Name is required';
    if (!formData.gpName?.trim()) errors.gpName = 'GP / Sponsor is required';
    if (!formData.commitmentAmount) errors.commitmentAmount = 'Commitment Amount is required';
    if (formData.commitmentAmount && parseAmountShorthand(formData.commitmentAmount) <= 0) {
      errors.commitmentAmount = 'Must be > 0';
    }
    if (!formData.commitmentDate) errors.commitmentDate = 'Commitment Date is required';
    if (!formData.tradeDate) errors.tradeDate = 'Trade Date is required';
    if (!formData.settleDate) errors.settleDate = 'Settle Date is required';

    if (formData.vintageYear) {
      const vy = parseInt(formData.vintageYear, 10);
      const currentYear = fromIso(getTenantToday()).getFullYear();
      if (isNaN(vy) || vy < 1980 || vy > currentYear + 2) {
        errors.vintageYear = `Vintage must be between 1980 and ${currentYear + 2}`;
      }
    }

    if (action === 'execute') {
      if (!formData.executedBy?.trim()) errors.executedBy = 'Executed By is required for execution';
    }

    return errors;
  };

  const buildTransactionData = async (formData: any, dimensionData: any): Promise<any> => {
    const portfolio = dimensionData.portfolios.find(
      (p: any) => p.portfolioName === formData.portfolio.trim()
    );
    if (!portfolio) {
      throw new Error(`Portfolio "${formData.portfolio}" not found`);
    }

    const commitmentAmount = parseAmountShorthand(formData.commitmentAmount);

    // Step 1 — synthesise the SMF only (no silo write at DRAFT). The
    // fact_commitment silo is materialised by CommitmentPostingSpi when the
    // ACQUIRE trade reaches POSTED, using the PE metadata columns sent on the
    // trade payload below. Mirrors RE Equity /api/credit/re-equity/prepare.
    //
    // Fund identity flows on the prepare body so the backend can find-or-create
    // dim_private_fund and stamp the SMF with a meaningful security_name (the
    // blotter / positions screen / dashboards all share that identity instead
    // of falling back to the generic "PE Commitment" label).
    const preparePayload: Record<string, any> = {
      portfolioId: portfolio.id,
      commitmentAmount: commitmentAmount,
      commitmentCurrency: formData.currency || 'USD',
      fundName: formData.fundName?.trim() || null,
      gpName: formData.gpName?.trim() || null,
      vintageYear: formData.vintageYear ? parseInt(formData.vintageYear, 10) : null,
      fundStructure: formData.fundStructure || null,
      managementFeeRate: formData.managementFeeRate
        ? parseFloat(formData.managementFeeRate) : null,
      carriedInterestRate: formData.carriedInterestRate
        ? parseFloat(formData.carriedInterestRate) : null,
      preferredReturnRate: formData.preferredReturnRate
        ? parseFloat(formData.preferredReturnRate) : null,
    };
    const prepareRes = await axios.post('/api/alternatives/commitments/prepare', preparePayload);
    const { securityId, sourceRef, fundId } = prepareRes.data;

    // Resolve dropdown names → dimension ids.
    let assetManagerId: number | null = null;
    if (formData.assetManager && formData.assetManager.trim()) {
      const am = dimensionData.assetManagers.find(
        (a: any) => a.assetManagerName === formData.assetManager.trim()
      );
      if (am?.id) assetManagerId = am.id;
    }
    let portfolioManagerId: number | null = null;
    if (formData.portfolioManager && formData.portfolioManager.trim()) {
      const pm = dimensionData.portfolioManagers.find(
        (p: any) => p.portfolioManagerName === formData.portfolioManager.trim()
      );
      if (pm?.id) portfolioManagerId = pm.id;
    }

    // Step 2 — full trade payload. PE columns ride on the trade so
    // CommitmentPostingSpi.ensureSiloFromTrade can rebuild fact_commitment at
    // POST. quantity = commitmentAmount, price = 100 → amount =
    // commitmentAmount, mirroring buildAcquireTrade in
    // PrivateEquityCommitmentTradingService.
    return {
      transactionType: formData.transactionType,
      securityId: securityId,
      portfolioId: portfolio.id,
      assetManagerId: assetManagerId,
      portfolioManagerId: portfolioManagerId,
      quantity: commitmentAmount,
      price: 100,
      amount: commitmentAmount,
      tradeDate: formData.tradeDate,
      settleDate: formData.settleDate,
      notes: formData.notes
        ? `${formData.notes}\nFund: ${formData.fundName} (GP: ${formData.gpName})`
        : `Fund: ${formData.fundName} (GP: ${formData.gpName})`,
      createdBy: formData.submittedBy,
      executedBy: formData.executedBy || null,
      status: 'DRAFT',
      ticketType: 'PE_COMMITMENT',
      eventType: 'ACQUIRE',
      sourceRef: sourceRef,
      currency: formData.currency || 'USD',
      // PE metadata (V282) — read by CommitmentPostingSpi at POST time.
      peFundId: fundId || null,
      peStrategy: formData.strategy || null,
      peFundStructure: formData.fundStructure || null,
      peVintageYear: formData.vintageYear ? parseInt(formData.vintageYear, 10) : null,
      peManagementFeeRate: formData.managementFeeRate
        ? parseFloat(formData.managementFeeRate) : null,
      peCarriedInterestRate: formData.carriedInterestRate
        ? parseFloat(formData.carriedInterestRate) : null,
      pePreferredReturnRate: formData.preferredReturnRate
        ? parseFloat(formData.preferredReturnRate) : null,
    };
  };

  const loadTransactionData = (transaction: any) => {
    // Trade is the source of truth at read time. The fund/GP names live in
    // notes (we don't have a dim_pe_fund row guaranteed at DRAFT).
    const noteFundMatch = (transaction.notes || '').match(/Fund:\s*(.+?)(?:\s+\(GP:\s*(.+?)\))?$/m);
    const fundName = noteFundMatch?.[1]?.trim() || '';
    const gpName = noteFundMatch?.[2]?.trim() || '';

    return {
      transactionType: transaction.transactionType || 'BUY',
      assetManager: transaction.assetManagerName || transaction.assetManager || '',
      portfolioManager: transaction.portfolioManagerName || transaction.portfolioManager || '',
      assetClass: transaction.assetClassName || 'Private Equity',
      assetSubClass: transaction.assetSubClassName || 'PE Commitment',
      security: transaction.securityName || '',
      identifier: transaction.cusip || transaction.isin || transaction.identifier || '',
      portfolio: transaction.portfolioName || transaction.portfolio || '',
      amount: transaction.amount ? transaction.amount.toString() : '',
      tradeDate: transaction.tradeDate || getTodayDate(),
      // PE settles on commitment date — see line 60 comment.
      settleDate: transaction.settleDate || transaction.tradeDate || getTodayDate(),
      submittedBy: transaction.createdBy || getCurrentOperatorName(),
      currency: transaction.currency || 'USD',
      executedBy: transaction.executedBy || '',
      notes: transaction.notes || '',
      // PE-specific — sourced from fact_trade_transactions PE columns
      fundName,
      gpName,
      strategy: transaction.peStrategy || 'BUYOUT',
      fundStructure: transaction.peFundStructure || 'LIMITED_PARTNERSHIP',
      vintageYear: transaction.peVintageYear != null
        ? transaction.peVintageYear.toString()
        : fromIso(getTenantToday()).getFullYear().toString(),
      commitmentDate: transaction.tradeDate || getTodayDate(),
      commitmentAmount: transaction.amount ? transaction.amount.toString() : '',
      // Fee rates: UI labels are "(%)" so display percentages.
      // Storage convention is mixed across legacy paths (UI saved 2.0 = 2%,
      // doc-ingestion saves 0.02 = 2%). Heuristic: anything < 1.0 is treated
      // as a decimal and rescaled for display. PE-BUG-8 tracks normalising
      // storage to decimals everywhere.
      managementFeeRate: transaction.peManagementFeeRate != null
        ? (transaction.peManagementFeeRate < 1
            ? (transaction.peManagementFeeRate * 100).toString()
            : transaction.peManagementFeeRate.toString())
        : '2.0',
      carriedInterestRate: transaction.peCarriedInterestRate != null
        ? (transaction.peCarriedInterestRate < 1
            ? (transaction.peCarriedInterestRate * 100).toString()
            : transaction.peCarriedInterestRate.toString())
        : '20.0',
      preferredReturnRate: transaction.pePreferredReturnRate != null
        ? (transaction.pePreferredReturnRate < 1
            ? (transaction.pePreferredReturnRate * 100).toString()
            : transaction.pePreferredReturnRate.toString())
        : '8.0',
    };
  };

  return (
    <BaseTicketScreen
      title="Private Equity Commitment Trade Entry"
      assetClass="Private Equity"
      assetSubClass="PE Commitment"
      initialFormData={initialFormData}
      validateForm={validateForm}
      buildTransactionData={buildTransactionData}
      loadTransactionData={loadTransactionData}
    >
      {({ formData, setFormData, errors, dimensionData, transactionStatus,
          isFieldDisabled, handleInputChange, handleInputBlur }) => (
        <StructuredWorkspace>
          <StructuredHeroPanel
            toneName="pe"
            icon={Target}
            eyebrow="Private Equity"
            title="PE Commitment Ticket"
            description="Book a fund commitment through the standard trade lifecycle while preserving the fund, GP, vintage, and economic terms needed by the commitment posting path."
            badges={['BUY-only acquisition', 'Commitment anchored', 'Follow-ons from dashboard']}
            signals={[
              { label: 'Fund', value: formData.fundName || 'Select fund', accent: true },
              { label: 'Commitment', value: formatCommitmentSignal(formData.commitmentAmount, formData.currency), accent: true },
              { label: 'Strategy', value: formData.strategy || '-' },
              { label: 'Vintage', value: formData.vintageYear || '-' },
            ]}
          />

          <StructuredBand>
            <BandHeader>
              <BandTitleGroup>
                <BandEyebrow $tone="pe">Trade Setup</BandEyebrow>
                <BandTitle>Dates, Ownership, and Execution</BandTitle>
                <BandSubline>Set the commitment trade date and operating ownership before moving the ticket through workflow.</BandSubline>
              </BandTitleGroup>
            </BandHeader>

          {/* ── Row 1: Transaction Type, Trade Date, Settle Date ── */}
          <FormRow>
            <FormGroup>
              <Label htmlFor="transactionType">Transaction Type*</Label>
              <Select
                id="transactionType"
                value={formData.transactionType || 'BUY'}
                onChange={() => { /* read-only: PE commitments are BUY-only at entry */ }}
                disabled
              >
                <option value="BUY">Acquire (Commit)</option>
              </Select>
            </FormGroup>

            <FormGroup>
              <Label htmlFor="tradeDate">Trade Date*</Label>
              <DateField
                id="tradeDate"
                value={formData.tradeDate ?? ''}
                className={errors.tradeDate ? 'error' : ''}
                disabled={isFieldDisabled('tradeDate')}
                calendars={calendarsFor('PE_COMMITMENT', formData.currency)}
                onChange={iso => { handleInputChange('tradeDate', iso); handleInputBlur('tradeDate', iso); }}
              />
              {errors.tradeDate && <FieldError>{errors.tradeDate}</FieldError>}
            </FormGroup>

            <FormGroup>
              <Label htmlFor="settleDate">Settle Date*</Label>
              <DateField
                id="settleDate"
                value={formData.settleDate ?? ''}
                className={errors.settleDate ? 'error' : ''}
                disabled={isFieldDisabled('settleDate')}
                calendars={settlementCalendars({ ticketType: 'PE_COMMITMENT', currency: formData.currency })}
                onChange={iso => handleInputChange('settleDate', iso)}
              />
              {errors.settleDate && <FieldError>{errors.settleDate}</FieldError>}
            </FormGroup>
          </FormRow>

          {/* ── Row 2: Asset Manager, Portfolio Manager, Executed By ── */}
          <FormRow>
            <FormGroup>
              <Label htmlFor="assetManager">Asset Manager*</Label>
              <Select
                id="assetManager"
                value={formData.assetManager}
                onChange={(e) => handleInputChange('assetManager', e.target.value)}
                disabled={isFieldDisabled('assetManager')}
              >
                <option value="">Select Asset Manager</option>
                {dimensionData.assetManagers.map((am: any) => (
                  <option key={am.id} value={am.assetManagerName}>{am.assetManagerName}</option>
                ))}
              </Select>
            </FormGroup>

            <FormGroup>
              <Label htmlFor="portfolioManager">Portfolio Manager</Label>
              <Select
                id="portfolioManager"
                value={formData.portfolioManager}
                onChange={(e) => handleInputChange('portfolioManager', e.target.value)}
                disabled={isFieldDisabled('portfolioManager')}
              >
                <option value="">Select Portfolio Manager</option>
                {dimensionData.portfolioManagers.map((pm: any) => (
                  <option key={pm.id} value={pm.portfolioManagerName}>{pm.portfolioManagerName}</option>
                ))}
              </Select>
            </FormGroup>

            <FormGroup>
              <Label htmlFor="executedBy">Executed By{transactionStatus === 'SENT' ? '*' : ''}</Label>
              <Select
                id="executedBy"
                value={formData.executedBy}
                onChange={(e) => handleInputChange('executedBy', e.target.value)}
                onBlur={(e) => handleInputBlur('executedBy', e.target.value)}
                disabled={isFieldDisabled('executedBy')}
                className={errors.executedBy ? 'error' : ''}
              >
                <option value="">Select</option>
                {dimensionData.users.map((user: any) => (
                  <option key={user.id} value={user.fullName}>
                    {user.fullName}{user.role ? ` (${user.role})` : ''}
                  </option>
                ))}
              </Select>
              {errors.executedBy && <FieldError>{errors.executedBy}</FieldError>}
            </FormGroup>
          </FormRow>
          </StructuredBand>

          {/* ── Fund Identification ── */}
          <StructuredBand>
          <BandHeader>
            <BandTitleGroup>
              <BandEyebrow $tone="pe">Fund Identification</BandEyebrow>
              <BandTitle>Fund, GP, Strategy, and Vintage</BandTitle>
              <BandSubline>Define the private fund identity that will be stamped onto the prepared security and trade metadata.</BandSubline>
            </BandTitleGroup>
          </BandHeader>
          <FormRow>
            <FormGroup style={{ flex: 2 }}>
              <Label htmlFor="fundName">Fund Name*</Label>
              <Input
                id="fundName"
                type="text"
                value={formData.fundName}
                onChange={(e) => {
                  handleInputChange('fundName', e.target.value);
                  handleInputChange('security', e.target.value);
                }}
                placeholder="e.g. Apollo Strategic Capital VIII"
                className={errors.fundName ? 'error' : ''}
                disabled={isFieldDisabled('fundName')}
              />
              {errors.fundName && <FieldError>{errors.fundName}</FieldError>}
            </FormGroup>

            <FormGroup>
              <Label htmlFor="gpName">GP / Sponsor*</Label>
              <Input
                id="gpName"
                type="text"
                value={formData.gpName}
                onChange={(e) => handleInputChange('gpName', e.target.value)}
                placeholder="e.g. Apollo Global Management"
                className={errors.gpName ? 'error' : ''}
                disabled={isFieldDisabled('gpName')}
              />
              {errors.gpName && <FieldError>{errors.gpName}</FieldError>}
            </FormGroup>
          </FormRow>

          <FormRow>
            <FormGroup>
              <Label htmlFor="strategy">Strategy</Label>
              <Select
                id="strategy"
                value={formData.strategy}
                onChange={(e) => handleInputChange('strategy', e.target.value)}
                disabled={isFieldDisabled('strategy')}
              >
                <option value="BUYOUT">Buyout</option>
                <option value="GROWTH">Growth Equity</option>
                <option value="VENTURE">Venture Capital</option>
                <option value="SECONDARY">Secondaries</option>
                <option value="FUND_OF_FUNDS">Fund of Funds</option>
                <option value="DISTRESSED">Distressed / Special Situations</option>
                <option value="INFRASTRUCTURE">Infrastructure</option>
              </Select>
            </FormGroup>

            <FormGroup>
              <Label htmlFor="fundStructure">Fund Structure</Label>
              <Select
                id="fundStructure"
                value={formData.fundStructure}
                onChange={(e) => handleInputChange('fundStructure', e.target.value)}
                disabled={isFieldDisabled('fundStructure')}
              >
                <option value="LIMITED_PARTNERSHIP">Limited Partnership (LP)</option>
                <option value="LLC">LLC</option>
                <option value="CORPORATION">Corporation</option>
                <option value="TRUST">Trust</option>
                <option value="SEPARATE_ACCOUNT">Separate Account</option>
              </Select>
            </FormGroup>

            <FormGroup>
              <Label htmlFor="vintageYear">Vintage Year</Label>
              <Input
                id="vintageYear"
                type="number"
                value={formData.vintageYear}
                onChange={(e) => handleInputChange('vintageYear', e.target.value)}
                placeholder="e.g. 2024"
                className={errors.vintageYear ? 'error' : ''}
                disabled={isFieldDisabled('vintageYear')}
              />
              {errors.vintageYear && <FieldError>{errors.vintageYear}</FieldError>}
            </FormGroup>
          </FormRow>
          </StructuredBand>

          {/* ── Commitment Terms ── */}
          <StructuredBand>
          <BandHeader>
            <BandTitleGroup>
              <BandEyebrow $tone="pe">Commitment Terms</BandEyebrow>
              <BandTitle>Portfolio, Amount, Currency, and Commitment Date</BandTitle>
              <BandSubline>Choose the booking portfolio and size the commitment that will anchor later calls and distributions.</BandSubline>
            </BandTitleGroup>
          </BandHeader>
          <FormRow>
            <FormGroup>
              <Label htmlFor="portfolio">Portfolio*</Label>
              <Select
                id="portfolio"
                value={formData.portfolio}
                onChange={(e) => handleInputChange('portfolio', e.target.value)}
                onBlur={(e) => handleInputBlur('portfolio', e.target.value)}
                className={errors.portfolio ? 'error' : ''}
                disabled={isFieldDisabled('portfolio')}
              >
                <option value="">Select Portfolio</option>
                {dimensionData.portfolios.map((p: any) => (
                  <option key={p.id} value={p.portfolioName}>{p.portfolioName}</option>
                ))}
              </Select>
              {errors.portfolio && <FieldError>{errors.portfolio}</FieldError>}
            </FormGroup>

            <FormGroup>
              <Label htmlFor="commitmentAmount">Commitment Amount ($)*</Label>
              <Input
                id="commitmentAmount"
                type="text"
                value={formData.commitmentAmount}
                onChange={(e) => handleInputChange('commitmentAmount', e.target.value)}
                onBlur={(e) => {
                  const parsed = parseAmountShorthand(e.target.value);
                  if (parsed > 0) {
                    const formatted = formatAmountWithCommas(parsed);
                    setFormData((prev: any) => ({
                      ...prev,
                      commitmentAmount: formatted,
                      amount: formatted,
                    }));
                  }
                  handleInputBlur('commitmentAmount', parsed.toString());
                }}
                placeholder="e.g. 25M, 25000000"
                className={errors.commitmentAmount ? 'error' : ''}
                disabled={isFieldDisabled('commitmentAmount')}
              />
              {errors.commitmentAmount && <FieldError>{errors.commitmentAmount}</FieldError>}
            </FormGroup>

            <FormGroup>
              <Label htmlFor="currency">Currency</Label>
              <Select
                id="currency"
                value={formData.currency}
                onChange={(e) => handleInputChange('currency', e.target.value)}
                disabled={isFieldDisabled('currency')}
              >
                <option value="">Select Currency</option>
                {dimensionData.currencies.map((c: any) => (
                  <option key={c.id} value={c.currencyCode}>
                    {c.currencyCode} - {c.currencyName}
                  </option>
                ))}
              </Select>
            </FormGroup>
          </FormRow>

          <FormRow>
            <FormGroup>
              <Label htmlFor="commitmentDate">Commitment Date*</Label>
              <DateField
                id="commitmentDate"
                value={formData.commitmentDate ?? ''}
                className={errors.commitmentDate ? 'error' : ''}
                disabled={isFieldDisabled('commitmentDate')}
                onChange={iso => handleInputChange('commitmentDate', iso)}
              />
              {errors.commitmentDate && <FieldError>{errors.commitmentDate}</FieldError>}
            </FormGroup>
          </FormRow>
          </StructuredBand>

          {/* ── Economic Terms ── */}
          <StructuredBand>
          <BandHeader>
            <BandTitleGroup>
              <BandEyebrow $tone="pe">Economic Terms</BandEyebrow>
              <BandTitle>Fees, Carry, and Preferred Return</BandTitle>
              <BandSubline>Capture headline private fund economics as trade metadata for the commitment record.</BandSubline>
            </BandTitleGroup>
          </BandHeader>
          <FormRow>
            <FormGroup>
              <Label htmlFor="managementFeeRate">Management Fee (% p.a.)</Label>
              <Input
                id="managementFeeRate"
                type="number"
                step="0.01"
                value={formData.managementFeeRate}
                onChange={(e) => handleInputChange('managementFeeRate', e.target.value)}
                placeholder="e.g. 2.0"
                disabled={isFieldDisabled('managementFeeRate')}
              />
            </FormGroup>

            <FormGroup>
              <Label htmlFor="carriedInterestRate">Carried Interest (%)</Label>
              <Input
                id="carriedInterestRate"
                type="number"
                step="0.01"
                value={formData.carriedInterestRate}
                onChange={(e) => handleInputChange('carriedInterestRate', e.target.value)}
                placeholder="e.g. 20.0"
                disabled={isFieldDisabled('carriedInterestRate')}
              />
            </FormGroup>

            <FormGroup>
              <Label htmlFor="preferredReturnRate">Preferred Return / Hurdle (%)</Label>
              <Input
                id="preferredReturnRate"
                type="number"
                step="0.01"
                value={formData.preferredReturnRate}
                onChange={(e) => handleInputChange('preferredReturnRate', e.target.value)}
                placeholder="e.g. 8.0"
                disabled={isFieldDisabled('preferredReturnRate')}
              />
            </FormGroup>
          </FormRow>
          </StructuredBand>

          <InfoNote>
            Capital calls, distributions, NAV refreshes, and secondary sales originate from the
            <strong> PE Commitments dashboard</strong> after this trade reaches POSTED — not from
            this ticket. The ticket is BUY-only, matching the position-anchored asset-class pattern
            used by RE Equity and Private Credit.
          </InfoNote>
        </StructuredWorkspace>
      )}
    </BaseTicketScreen>
  );
};

export default PrivateEquityTicketScreen;
